import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ImagePlus, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Button, Field, Loading } from "@/components/kit";
import { PageHeader, StickyBar } from "@/components/layout";
import { useToast } from "@/components/toast";
import { api, uploadFile } from "@/lib/api";
import { cn } from "@/lib/utils";

// Brand swatches are user data (rendered into their video), not app theme colors.
const PRIMARY = ["#14213D", "#0B0B0F", "#1F3A2E", "#5B1A2B", "#2F3542", "#1E3A8A"];
const ACCENT = ["#C9A227", "#E8D8B0", "#B87333", "#FFFFFF", "#2BB3A3", "#D98CA0"];
const HEX = /^#?[0-9A-Fa-f]{6}$/;
const norm = (h) => (h.startsWith("#") ? h : `#${h}`).toUpperCase();

function Swatches({ label, value, options, onChange, testId }) {
  const valid = HEX.test(value);
  return (
    <fieldset className="flex flex-col gap-3">
      <legend className="mb-3 text-sm font-medium">{label}</legend>
      <div className="flex flex-wrap gap-3">
        {options.map((c) => {
          const on = valid && norm(value) === c;
          return (
            <button key={c} type="button" aria-label={`${label} ${c}`} aria-pressed={on} onClick={() => onChange(c)} data-testid={`${testId}-swatch-${c.slice(1)}`}
              className={cn("focus-ring h-11 w-11 rounded-full border-2 border-input transition-transform hover:scale-105", on && "scale-110 border-[3px] border-primary")}
              style={{ backgroundColor: c }} />
          );
        })}
      </div>
      <Field id={`${testId}-hex`} label="Hex" value={value} onChange={(e) => onChange(e.target.value)} placeholder="#14213D" className="max-w-[220px]"
        inputClassName="uppercase" error={value && !valid ? "Use a 6-digit hex like #14213D" : null} data-testid={`${testId}-hex-input`} />
    </fieldset>
  );
}

function EndCard({ f }) {
  const prim = HEX.test(f.primary_color) ? norm(f.primary_color) : "#14213D";
  const acc = HEX.test(f.accent_color) ? norm(f.accent_color) : "#C9A227";
  const ink = prim === "#FFFFFF" ? "#111111" : "#FFFFFF";
  return (
    <div className="flex flex-col gap-3">
      <h2 className="text-sm font-medium">End card preview</h2>
      <div className="flex aspect-[9/12] max-h-[420px] flex-col items-center justify-center gap-2 rounded-[20px] p-6 text-center shadow-2xl transition-colors"
        style={{ backgroundColor: prim }} data-testid="branding-preview">
        <p className="text-[22px] font-semibold" style={{ color: ink }} data-testid="branding-preview-name">{f.agent_name || "Your name"}</p>
        {f.agency_name ? <p className="text-xs font-medium tracking-[0.2em]" style={{ color: acc }} data-testid="branding-preview-agency">{f.agency_name.toUpperCase()}</p> : null}
        <span className="my-1 h-[3px] w-12 rounded-sm" style={{ backgroundColor: acc }} />
        <p className="text-lg font-medium" style={{ color: ink }} data-testid="branding-preview-phone">{f.phone || "Your phone"}</p>
      </div>
    </div>
  );
}

function LogoPicker({ logo, progress, onPick }) {
  const input = useRef(null);
  return (
    <>
      <button type="button" onClick={() => input.current?.click()} data-testid="branding-logo-button" aria-label={logo ? "Change logo" : "Upload logo"}
        className="focus-ring relative flex h-40 items-center justify-center overflow-hidden rounded-[20px] border border-dashed border-accent bg-primary/15 transition-colors hover:bg-primary/20">
        {logo ? <img src={logo} alt="Your logo" className="h-4/5 w-4/5 object-contain" data-testid="branding-logo-image" /> : (
          <span className="flex flex-col items-center gap-2"><ImagePlus className="h-8 w-8 text-primary" aria-hidden /><span className="text-sm font-medium">Upload logo</span></span>
        )}
        {progress !== null ? (
          <span className="glass absolute inset-0 flex items-center justify-center gap-2 text-sm font-medium" data-testid="branding-logo-progress">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Uploading {Math.round(progress * 100)}%
          </span>
        ) : null}
      </button>
      <input ref={input} type="file" accept="image/*" className="sr-only" tabIndex={-1} aria-hidden data-testid="branding-logo-input"
        onChange={(e) => { const file = e.target.files?.[0]; e.target.value = ""; if (file) onPick(file); }} />
    </>
  );
}

export default function BrandingPage() {
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ["branding"], queryFn: () => api("/branding") });
  const [f, setF] = useState({ agent_name: "", phone: "", agency_name: "", email: "", primary_color: "#14213D", accent_color: "#C9A227" });
  const [logo, setLogo] = useState(null);
  const [logoProgress, setLogoProgress] = useState(null);
  const [saving, setSaving] = useState(false);
  const seeded = useRef(false);

  // Seed the form from the server ONCE; later cache updates (e.g. logo upload) must not wipe typed fields.
  useEffect(() => {
    if (!q.data || seeded.current) return;
    seeded.current = true;
    const d = q.data;
    setF({
      agent_name: d.agent_name ?? "", phone: d.phone ?? "", agency_name: d.agency_name ?? "", email: d.email ?? "",
      primary_color: d.primary_color ?? "#14213D", accent_color: d.accent_color ?? "#C9A227",
    });
    setLogo(d.logo_url ?? null);
  }, [q.data]);

  if (!q.data) {
    return <section data-testid="branding-screen"><PageHeader title="Branding kit" />
      {q.isError ? <p className="text-card-foreground" data-testid="branding-load-error">{q.error.message}</p> : <Loading />}</section>;
  }
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: typeof v === "string" ? v : v.target.value }));
  const colorsValid = HEX.test(f.primary_color) && HEX.test(f.accent_color);

  const pickLogo = async (file) => {
    setLogoProgress(0);
    try {
      const b = await uploadFile("/branding/logo", file, setLogoProgress);
      setLogo(b.logo_url ?? null);
      qc.setQueryData(["branding"], (old) => (old ? { ...old, logo_media_id: b.logo_media_id, logo_url: b.logo_url } : b));
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setLogoProgress(null);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const b = await api("/branding", { method: "PUT", body: { ...f, primary_color: norm(f.primary_color), accent_color: norm(f.accent_color) } });
      qc.setQueryData(["branding"], b);
      await qc.invalidateQueries({ queryKey: ["branding"] });
      toast("Branding saved", "success");
      navigate(window.history.state?.idx > 0 ? -1 : "/projects");
    } catch (e) {
      toast(e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section data-testid="branding-screen">
      <PageHeader title="Branding kit" eyebrow="Your brand" />
      <p className="-mt-2 mb-8 text-base text-card-foreground">Shown on every video: logo, end card and title colors.</p>
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start">
        <div className="flex flex-col gap-5">
          <LogoPicker logo={logo} progress={logoProgress} onPick={pickLogo} />
          <div className="grid gap-5 sm:grid-cols-2">
            <Field id="branding-name" label="Display name" value={f.agent_name} onChange={set("agent_name")} placeholder="Jordan Lee" data-testid="branding-name-input" />
            <Field id="branding-phone" label="Phone" type="tel" value={f.phone} onChange={set("phone")} placeholder="(619) 555-0142" data-testid="branding-phone-input" />
            <Field id="branding-agency" label="Agency name" value={f.agency_name} onChange={set("agency_name")} placeholder="Harbor & Oak Realty" data-testid="branding-agency-input" />
            <Field id="branding-email" label="Email (optional)" type="email" value={f.email} onChange={set("email")} data-testid="branding-email-input" />
          </div>
          <div className="grid gap-6 rounded-[20px] border border-border bg-card p-5 sm:grid-cols-2">
            <Swatches label="Primary color" value={f.primary_color} options={PRIMARY} onChange={set("primary_color")} testId="branding-primary" />
            <Swatches label="Accent color" value={f.accent_color} options={ACCENT} onChange={set("accent_color")} testId="branding-accent" />
          </div>
        </div>
        <div className="lg:sticky lg:top-10"><EndCard f={f} /></div>
      </div>
      <StickyBar>
        <Button title="Save branding" onClick={save} loading={saving} disabled={!colorsValid} className="sm:min-w-[240px]" data-testid="branding-save-button" />
      </StickyBar>
    </section>
  );
}
